import { supportService } from "@/services/api/support-service";
import type {
	CreateStaffTicketRequest,
	SupportSettings,
	SupportTicketListParams,
	SupportTicketStatus,
	UpdateSupportTicketRequest,
} from "@/types/api/support";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const SUPPORT_KEYS = {
	all: ["support"] as const,
	list: (params: SupportTicketListParams) =>
		["support", "list", params] as const,
	summary: ["support", "summary"] as const,
	detail: (id: string) => ["support", "detail", id] as const,
	assignees: ["support", "assignees"] as const,
	settings: ["support", "settings"] as const,
};

export function useSupportTickets(params: SupportTicketListParams) {
	return useQuery({
		queryKey: SUPPORT_KEYS.list(params),
		queryFn: () => supportService.list(params),
		refetchInterval: 60_000,
	});
}

/** Drives the sidebar badge; polls so a new ticket shows up without a reload. */
export function useSupportSummary(enabled = true) {
	return useQuery({
		queryKey: SUPPORT_KEYS.summary,
		queryFn: supportService.summary,
		enabled,
		refetchInterval: 60_000,
		refetchOnWindowFocus: true,
	});
}

export function useSupportTicket(ticketId: string) {
	return useQuery({
		queryKey: SUPPORT_KEYS.detail(ticketId),
		queryFn: () => supportService.get(ticketId),
		enabled: !!ticketId,
		// Customer replies arrive while staff have the ticket open.
		refetchInterval: 30_000,
		refetchOnWindowFocus: true,
	});
}

export function useSupportAssignees() {
	return useQuery({
		queryKey: SUPPORT_KEYS.assignees,
		queryFn: supportService.assignees,
		staleTime: 5 * 60_000,
	});
}

function useInvalidateSupport() {
	const qc = useQueryClient();
	return () => qc.invalidateQueries({ queryKey: SUPPORT_KEYS.all });
}

export function useSendSupportMessage(ticketId: string) {
	const invalidate = useInvalidateSupport();
	return useMutation({
		mutationFn: async ({
			body,
			isInternal,
			files,
			setStatus,
		}: {
			body: string;
			isInternal: boolean;
			files: File[];
			setStatus?: SupportTicketStatus;
		}) => {
			const ids = files.length
				? await supportService.uploadFiles(ticketId, files)
				: [];
			return supportService.addMessage(
				ticketId,
				body,
				isInternal,
				ids,
				setStatus,
			);
		},
		onSuccess: invalidate,
	});
}

export function useUpdateSupportTicket(ticketId: string) {
	const invalidate = useInvalidateSupport();
	return useMutation({
		mutationFn: (request: UpdateSupportTicketRequest) =>
			supportService.update(ticketId, request),
		onSuccess: invalidate,
	});
}

export function useCreateStaffTicket() {
	const invalidate = useInvalidateSupport();
	return useMutation({
		mutationFn: (request: CreateStaffTicketRequest) =>
			supportService.create(request),
		onSuccess: invalidate,
	});
}

export function useSupportSettings() {
	return useQuery({
		queryKey: SUPPORT_KEYS.settings,
		queryFn: supportService.getSettings,
	});
}

export function useUpdateSupportSettings() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (settings: SupportSettings) =>
			supportService.updateSettings(settings),
		onSuccess: (saved) => qc.setQueryData(SUPPORT_KEYS.settings, saved),
	});
}
